import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import api from "../lib/api";
import { Trash2, Plus, Minus, ArrowRight } from "lucide-react";

export default function Cart() {
  const { items, update, remove, subtotal } = useCart();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [error, setError] = useState("");
  const nav = useNavigate();

  const shipping = items.length > 0 ? 15 : 0;
  const total = subtotal + shipping;

  const checkout = async () => {
    setError("");
    if (!email) { setError("Please enter your email for the receipt."); return; }
    setLoading(true);
    try {
      const payload = {
        items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        customer_email: email,
        customer_name: name,
        shipping_country: country,
      };
      const res = await api.post("/checkout/session", payload);
      window.location.href = res.data.url;
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="cart-empty" className="pt-40 pb-32 max-w-3xl mx-auto px-6 text-center">
        <h1 className="font-serif-display text-6xl tracking-tighter">Your cellar is empty.</h1>
        <p className="mt-4 text-[#4A4A4A]">Add some crimson, gold or wild forest jars.</p>
        <Link data-testid="empty-cart-shop-link" to="/shop" className="inline-flex mt-10 bg-[#C84B31] text-[#FAF9F6] px-8 py-4 uppercase tracking-overline text-sm hover:bg-[#A93F29]">
          Browse the Cellar
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="pt-28 pb-24 max-w-7xl mx-auto px-6 md:px-12">
      <p className="text-xs uppercase tracking-overline text-[#4A5D23] mb-4">Your Order</p>
      <h1 className="font-serif-display text-6xl md:text-7xl tracking-tighter mb-12">Cart</h1>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-8">
          {items.map((it) => (
            <div data-testid={`cart-item-${it.product_id}`} key={it.product_id} className="grid grid-cols-12 gap-4 border-b border-[#D5D1C5] pb-8">
              <div className="col-span-3">
                <div className="aspect-square bg-[#EAE6DB] overflow-hidden">
                  <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="col-span-9 flex flex-col justify-between">
                <div>
                  <h3 className="font-serif-display text-2xl tracking-tight">{it.name}</h3>
                  <p className="text-xs text-[#7A7A7A] uppercase tracking-overline mt-1">{it.unit_label}</p>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center border border-[#1A1A1A]">
                    <button data-testid={`cart-dec-${it.product_id}`} onClick={() => update(it.product_id, it.quantity - 1)} className="p-2 hover:bg-[#1A1A1A] hover:text-[#FAF9F6]"><Minus size={12}/></button>
                    <span className="px-4 text-sm tabular-nums">{it.quantity}</span>
                    <button data-testid={`cart-inc-${it.product_id}`} onClick={() => update(it.product_id, it.quantity + 1)} className="p-2 hover:bg-[#1A1A1A] hover:text-[#FAF9F6]"><Plus size={12}/></button>
                  </div>
                  <div className="flex items-center gap-6">
                    <span className="font-serif-display text-xl text-[#C84B31]">${(it.price_usd * it.quantity).toFixed(2)}</span>
                    <button data-testid={`cart-remove-${it.product_id}`} onClick={() => remove(it.product_id)} className="text-[#7A7A7A] hover:text-[#C84B31]"><Trash2 size={16}/></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-5">
          <div className="bg-[#F5F2EA] border border-[#D5D1C5] p-8 sticky top-28">
            <h2 className="font-serif-display text-3xl tracking-tight mb-6">Summary</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span className="tabular-nums">${subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-[#4A4A4A]"><span>International shipping</span><span className="tabular-nums">${shipping.toFixed(2)}</span></div>
              <div className="flex justify-between font-medium text-lg pt-3 border-t border-[#D5D1C5]"><span>Total (USD)</span><span data-testid="cart-total" className="font-serif-display text-2xl text-[#C84B31] tabular-nums">${total.toFixed(2)}</span></div>
            </div>

            <div className="mt-6 space-y-3">
              <input data-testid="checkout-name" placeholder="Full name" value={name} onChange={(e)=>setName(e.target.value)} className="w-full bg-[#FAF9F6] border border-[#D5D1C5] px-4 py-3 focus:border-[#1A1A1A] outline-none"/>
              <input data-testid="checkout-email" placeholder="Email for receipt *" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} className="w-full bg-[#FAF9F6] border border-[#D5D1C5] px-4 py-3 focus:border-[#1A1A1A] outline-none"/>
              <input data-testid="checkout-country" placeholder="Shipping country" value={country} onChange={(e)=>setCountry(e.target.value)} className="w-full bg-[#FAF9F6] border border-[#D5D1C5] px-4 py-3 focus:border-[#1A1A1A] outline-none"/>
            </div>

            {error && <p data-testid="checkout-error" className="text-[#C84B31] text-sm mt-4">{error}</p>}

            <button
              data-testid="checkout-btn"
              disabled={loading}
              onClick={checkout}
              className="w-full mt-6 bg-[#1A1A1A] text-[#FAF9F6] px-6 py-4 hover:bg-[#C84B31] transition-colors uppercase tracking-overline text-sm flex items-center justify-center gap-3 disabled:opacity-60"
            >
              {loading ? "Redirecting…" : (<>Pay securely with Stripe <ArrowRight size={16}/></>)}
            </button>
            <p className="text-xs text-[#7A7A7A] mt-4 text-center">Card payments via Stripe · USD · International.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
